import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { Button } from '@uk-source-web/button';
import { Heading } from '@uk-source-web/heading';
import { Icon } from '@uk-source-web/icon';

import { OrderCompletedPurchaseProps } from './OrderCompletedPurchase.types';

import CompletedPurchaseStyles, {
  CalloutMessage,
  CompletedPurchaseButtons,
  CompletedPurchaseCallout,
  CompletedPurchaseContainer,
  CompletedPurchaseContent,
  CompletedPurchaseHeader,
  CompletedPurchaseStepper,
} from './OrderCompletedPurchase.styles';
import { useLocation } from '@reach/router';
import { Paragraph } from '@uk-source-web/paragraph';
import { BasketHorizontalStepper } from '../SWBasket/BasketHorizontalStepper/BasketHorizontalStepper';
import handleTranslation from '@/helpers/handleTranslation';
import { useEffect, useMemo } from 'react';
import {getCurrentUser} from '../../../../utils/getCurrentUser';

const OrderCompletedPurchase = ({
  title,
  purchaseStatus,
  messageHeading,
  mainMessage,
  calloutMessages,
  vodafoneButton,
  continueButton,
}: OrderCompletedPurchaseProps) => {
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const orderId = queryParams.get('id') || 'teste@email.com';
  
  // move sessionStorage access into useMemo with typeof window check because of SSR stuff
  const { userEmail } = useMemo(() => {
    if (typeof window === 'undefined') {
      return { userEmail: '' };
    }
    
    try {
      const rawSession = sessionStorage.getItem('persist:vf_userSession');
      if (!rawSession) {
        return { userEmail: '' };
      }
      const parsed = JSON.parse(rawSession);
      const user = JSON.parse(parsed.user);
      return { userEmail: user?.username || '' };
    } catch (error) {
      console.error('Error parsing session:', error);
      return { userEmail: '' };
    }
  }, []);

  const emailMessage = handleTranslation(
    'An email will be sent to your Inbox with information about your order.',
    'Será enviado um email para a sua caixa de correio sobre a sua compra.'
  );

  const formatMainMessage = (msg: OrderCompletedPurchaseProps['mainMessage']) => {
    let message = msg.raw;
    message = message.replaceAll('${orderId}', orderId);
    message = message.replaceAll('${userEmail}', userEmail);

    return documentToReactComponents(JSON.parse(message));
  };

  
  useEffect(() => {
    const currentUser = getCurrentUser();
    window.utag?.view({
      event_name: 'page_view',
      page_name:'checkout:orderconfirmation',
      page_type:'Order Confirmation',
      journey_name:"Checkout - Order Confirmation",
      journey_tybe:"Order Confirmation",
      visitor_lid: currentUser?.username || 'Guest',
      customer_id: currentUser?.selectedAccount?.customerId || 'Unknown',
      username:currentUser?.selectedAccount?.customerAccountName || 'Unknown', 
    })
  },[])

  return (
    <>
      <CompletedPurchaseStepper>
        <BasketHorizontalStepper currentStep={3} />
      </CompletedPurchaseStepper>
      <CompletedPurchaseStyles>
        <CompletedPurchaseContainer>
          <CompletedPurchaseHeader>
            {purchaseStatus === 'success' ? (
              <Icon name={'tick-outline'} group="hifiLight" />
            ) : (
              <Icon name={'error'} group="state" />
            )}

            <Heading level={3}>{title}</Heading>
            <Paragraph size={2} weight={3} justify="center">
              {emailMessage}
            </Paragraph>
          </CompletedPurchaseHeader>
          <CompletedPurchaseContent>
            <Heading level={5} weight={4}>
              {messageHeading}
            </Heading>
            <CompletedPurchaseCallout>
              {purchaseStatus === 'success' ? (
                <Icon name={'tick-circle'} group="system" />
              ) : (
                <Icon name={'error'} group="state" />
              )}
              <CalloutMessage>{formatMainMessage(mainMessage)}</CalloutMessage>
            </CompletedPurchaseCallout>
            {calloutMessages?.map(callout => (
              <CompletedPurchaseCallout key={callout.id}>
                {callout?.icon && <Icon name={callout.icon} group="system" size={3} />}
                <CalloutMessage>{documentToReactComponents(JSON.parse(callout.message.raw))}</CalloutMessage>
              </CompletedPurchaseCallout>
            ))}
          </CompletedPurchaseContent>
          <CompletedPurchaseButtons>
            <Button
              id="completed-purchase-vodafone"
              text={vodafoneButton.label}
              href={vodafoneButton?.url}
              icon={vodafoneButton?.iconRef ? { name: vodafoneButton.iconRef } : undefined}
              appearance="primary"
            />
          </CompletedPurchaseButtons>
        </CompletedPurchaseContainer>
      </CompletedPurchaseStyles>
    </>
  );
};

export default OrderCompletedPurchase;